import { AuthProvider, AuthState, AuthTokens, UserInfo, LoginOptions, AuthEvent, AuthEventListener } from './types';
import { defaultAuthConfig, isAuthEnabled, getCurrentProviderConfig } from '../config/auth';
import { KeycloakProvider } from './providers/KeycloakProvider';
import { tokenStorage } from './TokenStorage';

/**
 * Central authentication manager that coordinates all authentication providers
 * and manages the authentication state of the application
 */
export class AuthManager {
  private provider: AuthProvider | null = null;
  private state: AuthState = {
    isAuthenticated: false,
    isLoading: false,
    user: null,
    tokens: null,
    error: null,
  };
  private listeners: AuthEventListener[] = [];
  private refreshTimer: NodeJS.Timeout | null = null;
  private refreshInProgress: Promise<void> | null = null;

  constructor() {
    this.initializeProvider();
  }

  // ================== Public API ==================

  /**
   * Initialize the authentication system
   */
  async initialize(): Promise<void> {
    if (!isAuthEnabled()) {
      return; // No authentication needed
    }

    this.setState({ isLoading: true });

    try {
      // Check if we have stored tokens
      const tokens = await this.provider?.getTokens();
      
      if (tokens && this.provider?.isTokenValid(tokens)) {
        // We have valid tokens, get user info
        const user = await this.provider.getUserInfo(tokens);
        this.setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          tokens,
          error: null,
        });
        
        this.startTokenRefreshTimer(tokens);
        this.emitEvent({ type: 'LOGIN_SUCCESS', tokens, user });
      } else if (tokens && !this.provider?.isTokenValid(tokens)) {
        // Try to refresh tokens
        try {
          const refreshedTokens = await this.provider.refreshTokens();
          const user = await this.provider.getUserInfo(refreshedTokens);
          
          this.setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            tokens: refreshedTokens,
            error: null,
          });
          
          this.startTokenRefreshTimer(refreshedTokens);
          this.emitEvent({ type: 'TOKEN_REFRESH_SUCCESS', tokens: refreshedTokens });
        } catch (error) {
          // Refresh failed, clear tokens and require login
          tokenStorage.clearTokens();
          this.setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            tokens: null,
            error: null,
          });
          this.emitEvent({ type: 'SESSION_EXPIRED' });
        }
      } else {
        // No valid tokens, user needs to login
        this.setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tokens: null,
          error: null,
        });
      }
    } catch (error) {
      logger.error('Error initializing authentication:', error);
      this.setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: error instanceof Error ? error.message : 'Authentication initialization failed',
      });
    }
  }

  /**
   * Login with the configured provider
   */
  async login(options?: LoginOptions): Promise<void> {
    if (!this.provider) {
      throw new Error('No authentication provider configured');
    }

    this.setState({ isLoading: true, error: null });
    this.emitEvent({ type: 'LOGIN_START' });

    try {
      await this.provider.login(options);
      
      // After successful login, get tokens and user info
      const tokens = await this.provider.getTokens();
      
      if (!tokens) {
        throw new Error('No tokens received after login');
      }

      const user = await this.provider.getUserInfo(tokens);
      
      this.setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        tokens,
        error: null,
      });

      this.startTokenRefreshTimer(tokens);
      this.emitEvent({ type: 'LOGIN_SUCCESS', tokens, user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      this.setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: errorMessage,
      });
      this.emitEvent({ type: 'LOGIN_ERROR', error: errorMessage });
      throw error;
    }
  }

  /**
   * Logout and clear all authentication data
   */
  async logout(): Promise<void> {
    this.setState({ isLoading: true });

    try {
      await this.provider?.logout();
    } catch (error) {
      logger.error('Error during logout:', error);
    }

    this.clearRefreshTimer();
    this.setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      tokens: null,
      error: null,
    });
    
    this.emitEvent({ type: 'LOGOUT' });
  }

  /**
   * Handle authentication callback (for OAuth flows)
   */
  async handleCallback(): Promise<void> {
    if (!this.provider) {
      throw new Error('No authentication provider configured');
    }

    try {
      const tokens = await this.provider.handleCallback();
      
      const user = await this.provider.getUserInfo(tokens);
      
      this.setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        tokens,
        error: null,
      });

      this.startTokenRefreshTimer(tokens);
      this.emitEvent({ type: 'LOGIN_SUCCESS', tokens, user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Callback handling failed';
      this.setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: errorMessage,
      });
      this.emitEvent({ type: 'LOGIN_ERROR', error: errorMessage });
      throw error;
    }
  }

  /**
   * Get current authentication state
   */
  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Get current access token (for API calls)
   */
  async getAccessToken(): Promise<string | null> {
    if (!this.state.isAuthenticated || !this.state.tokens) {
      return null;
    }

    // Check if token needs refresh
    if (tokenStorage.willExpireSoon(this.state.tokens)) {
      try {
        // If a refresh is already in progress, wait for it to complete
        if (this.refreshInProgress) {
          await this.refreshInProgress;
        } else {
          // Start a new refresh
          this.refreshInProgress = this.refreshTokens();
          await this.refreshInProgress;
          this.refreshInProgress = null;
        }
        
        // Re-check state after refresh (tokens might have been cleared on failure)
        if (!this.state.tokens) {
          return null;
        }
        
        return this.state.tokens.accessToken;
      } catch (error) {
        this.refreshInProgress = null;
        return null;
      }
    }

    return this.state.tokens.accessToken;
  }

  /**
   * Add authentication event listener
   */
  addEventListener(listener: AuthEventListener): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Check if authentication is enabled
   */
  isAuthenticationEnabled(): boolean {
    return isAuthEnabled();
  }

  /**
   * Get the current provider name
   */
  getProviderName(): string {
    return this.provider?.getProviderName() || 'None';
  }

  // ================== Private Methods ==================

  private initializeProvider(): void {
    const config = getCurrentProviderConfig();
    
    if (!config || !isAuthEnabled()) {
      this.provider = null;
      return;
    }

    switch (defaultAuthConfig.provider) {
      case 'keycloak':
        this.provider = new KeycloakProvider(config as any);
        break;
      case 'okta':
        // TODO: Implement OktaProvider
        throw new Error('Okta provider not yet implemented');
      case 'azure-ad':
        // TODO: Implement AzureAdProvider
        throw new Error('Azure AD provider not yet implemented');
      case 'google':
        // TODO: Implement GoogleProvider
        throw new Error('Google provider not yet implemented');
      case 'github':
        // TODO: Implement GitHubProvider
        throw new Error('GitHub provider not yet implemented');
      default:
        this.provider = null;
    }
  }

  private async refreshTokens(): Promise<void> {
    if (!this.provider || !this.state.tokens) {
      return;
    }

    try {
      const refreshedTokens = await this.provider.refreshTokens();
      
      this.setState({
        ...this.state,
        tokens: refreshedTokens,
      });

      this.startTokenRefreshTimer(refreshedTokens);
      this.emitEvent({ type: 'TOKEN_REFRESH_SUCCESS', tokens: refreshedTokens });
    } catch (error) {
      logger.error('Token refresh failed:', error);
      
      // If refresh fails, logout the user
      await this.logout();
      this.emitEvent({ type: 'TOKEN_REFRESH_ERROR', error: error instanceof Error ? error.message : 'Token refresh failed' });
    }
  }

  private startTokenRefreshTimer(tokens: AuthTokens): void {
    this.clearRefreshTimer();

    if (!tokens.expiresAt) {
      return; // No expiration time, can't set timer
    }

    // Refresh 5 minutes before expiration
    const refreshTime = tokens.expiresAt - Date.now() - (5 * 60 * 1000);
    
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshTokens();
      }, refreshTime);
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private setState(newState: Partial<AuthState>): void {
    this.state = { ...this.state, ...newState };
  }

  private emitEvent(event: AuthEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        logger.error('Error in auth event listener:', error);
      }
    });
  }
}

// Export singleton instance
export const authManager = new AuthManager(); 